import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, Loader2 } from 'lucide-react';

const fieldClasses =
  'flex h-11 w-full rounded-md border border-line bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-mute focus:border-brand/40 focus:outline-none focus:ring-1 focus:ring-brand/40 disabled:cursor-not-allowed disabled:opacity-50';

const labelClasses = 'font-mono text-[10px] uppercase tracking-[0.14em] text-fg-dim';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, user, userData, isSuperAdmin, isAdvertiser, advertiser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading || !user) return;

    if (isSuperAdmin) {
      navigate('/admin/advertisers', { replace: true });
      return;
    }

    if (!userData) return;

    if (!isAdvertiser) {
      navigate('/no-access', { replace: true });
      return;
    }

    if (!advertiser) {
      navigate('/no-access', { replace: true });
      return;
    }

    if (advertiser.status === 'active') {
      navigate('/', { replace: true });
    } else if (advertiser.status === 'paused') {
      navigate('/wallet', { replace: true });
    } else if (advertiser.status === 'pending') {
      navigate('/pending-approval', { replace: true });
    } else {
      navigate('/no-access', { replace: true });
    }
  }, [authLoading, user, userData, isSuperAdmin, isAdvertiser, advertiser, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
    } catch (err) {
      const fbError = err as { code?: string };
      if (
        fbError.code === 'auth/user-not-found' ||
        fbError.code === 'auth/wrong-password' ||
        fbError.code === 'auth/invalid-credential'
      ) {
        setError('Invalid email or password');
      } else if (fbError.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError('Failed to sign in. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-4 text-fg">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-brand font-serif-display text-lg text-brand-ink">
            g
          </div>
          <div className="flex items-baseline gap-1.5">
            <span className="font-serif-display text-xl leading-none text-fg">grobot</span>
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-mute">Ads</span>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-line bg-bg-elev">
          <div className="border-b border-line px-6 py-6 text-center">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-mute">Advertisers</div>
            <h1 className="mt-2 font-serif-display text-3xl leading-none text-fg">Welcome back</h1>
            <p className="mt-2 text-sm text-fg-dim">Sign in to your advertiser account</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6">
            {error && (
              <div className="flex items-start gap-3 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-danger" />
                <div className="text-fg-dim">{error}</div>
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="email" className={labelClasses}>
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className={fieldClasses}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className={labelClasses}>
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className={fieldClasses}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-brand text-sm font-medium text-brand-ink transition-colors hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign In
            </button>
          </form>

          <div className="space-y-2 border-t border-line px-6 py-5 text-center text-sm text-fg-mute">
            <p>
              New to Grobot ads?{' '}
              <Link to="/signup" className="font-medium text-brand underline underline-offset-2">
                Apply for advertiser access
              </Link>
              .
            </p>
            <p>
              Need help? Email{' '}
              <a href="mailto:support@grobotix.ai" className="font-medium text-brand underline underline-offset-2">
                support@grobotix.ai
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
